import React from 'react'
import { NameDto } from '@/api/Common/Types'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormField, FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { useAppSelector } from '@/store'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { FormikErrors, FormikProps, withFormik } from 'formik'

export interface CustomizeTransactionFormValues {
  transactionStateId: { id: number; name: string }
  amount: number
  rate: number
  originalAmount: number
  originalCurrency: string
  paymentProviderMethodId: string
  comment: string
  reason: string
  balanceDelta: number
  reservedDelta: number
  pendingDelta: number
}

interface OuterProps {
  transaction: TransactionDto
  alternativesPaymentProviders: NameDto<string>[]

  onSubmit(values: CustomizeTransactionFormValues): Promise<void>

  onCancel(): void
}

const CustomizeTransactionFormUI: React.FC<
  FormikProps<CustomizeTransactionFormValues> & OuterProps
> = (props) => {
  const { alternativesPaymentProviders, handleSubmit, isSubmitting } = props

  const transactionState = useAppSelector((state) => state.transactions.status)

  const statusOptions = transactionState.map((x) => ({
    id: x.id,
    name: x.name,
  }))

  const providerOptions = alternativesPaymentProviders.map((x) => ({
    id: x.id,
    name: x.name,
  }))

  return (
    <div className='overflow-y-auto'>
      <form className='w-full' onSubmit={handleSubmit}>
        <DataGrid columns={2}>
          <DataCard title='Details'>
            <FormSelectField name='transactionStateId' label='Status*' options={statusOptions} />

            <FormField name='amount' label='Amount*' type='number' />

            <FormField name='rate' label='Rate*' type='number' />

            <FormField name='originalAmount' label='Original Amount*' type='number' />

            <FormReadonlyField
              value={props.values.originalCurrency}
              name='originalCurrency'
              label='Original Currency*'
            />

            <FormSelectField
              name='paymentProviderMethodId'
              label='Payment Provider*'
              options={providerOptions}
            />

            <FormField name='comment' label='Comment' />

            <FormField name='reason' label='Reason*' />
          </DataCard>

          <DataCard title='Wallet Balance'>
            <FormField name='balanceDelta' label='Balance Delta*' type='number' />

            <FormField name='reservedDelta' label='Reserved Delta*' type='number' />

            <FormField name='pendingDelta' label='Pending Delta*' type='number' />
          </DataCard>

          <DataColumn colSpan={2}>
            <div className='flex justify-center'>
              <Button disabled={isSubmitting} type='submit' className='uppercase px-6'>
                Submit
              </Button>
            </div>
          </DataColumn>
        </DataGrid>
      </form>
    </div>
  )
}

export const CustomizeTransactionForm = withFormik<OuterProps, CustomizeTransactionFormValues>({
  mapPropsToValues: ({ transaction }): CustomizeTransactionFormValues => ({
    transactionStateId: {
      id: transaction.state.id,
      name: transaction.state.name,
    },
    amount: transaction.walletTransactions[0]?.amount ?? 0,
    rate: transaction.walletTransactions[0]?.currencyRate ?? 0,
    originalAmount: transaction.walletTransactions[0]?.otherAmount ?? 0,
    originalCurrency: transaction.walletTransactions[0]?.otherCurrency?.id ?? '',
    paymentProviderMethodId:
      transaction.walletTransactions[0]?.paymentProviderTransaction?.paymentProvider?.id ?? '',
    comment: transaction.comment ?? '',
    reason: '',
    balanceDelta: 0,
    reservedDelta: 0,
    pendingDelta: 0,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    await props.onSubmit(values)
    props.onCancel()
    setSubmitting(false)
  },
  validate: (values) => {
    const errors: FormikErrors<CustomizeTransactionFormValues> = {}
    if (!values.transactionStateId?.id) {
      errors.transactionStateId = { id: 'Required' }
    }
    if (!values.amount) {
      errors.amount = 'Required'
    }
    if (!values.rate) {
      errors.rate = 'Required'
    }
    if (!values.originalAmount) {
      errors.originalAmount = 'Required'
    }
    if (!values.originalCurrency) {
      errors.originalCurrency = 'Required'
    }
    if (!values.paymentProviderMethodId) {
      errors.paymentProviderMethodId = 'Required'
    }
    if (!values.reason) {
      errors.reason = 'Required'
    }
    if (!values.balanceDelta && values.balanceDelta !== 0) {
      errors.balanceDelta = 'Required'
    }
    if (!values.reservedDelta && values.reservedDelta !== 0) {
      errors.reservedDelta = 'Required'
    }
    if (!values.pendingDelta && values.pendingDelta !== 0) {
      errors.pendingDelta = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
})(CustomizeTransactionFormUI)
